// MAJLIS-154 (D-60, UI-C41 as amended 2026-10-02, consultant ruling 4; T-170): the summary's "Save as file".
//
// The file is ONE self-contained, SCRIPT-FREE HTML file, made ON THE PHONE from what the summary screen shows - nothing
// is sent to the server. No <script>, no event-handler attribute, no load of any kind (no src, no href, no url(), no
// font or stylesheet from anywhere): inline CSS only, and a Content-Security-Policy of default-src 'none' on top, so a
// browser that opens it loads and runs nothing even if something slipped past the escaping. It carries the title, the
// record header (day, start time and length; the people in their seat colours; the languages), the six sections in
// the screen's order (an empty one left out, Quick recap always), each notable moment with its name, time and stored
// words, and the Generated line. <html> carries the reading language's lang and dir. EVERY text from the summary or the
// record is HTML-escaped: model output and people's names are untrusted. No link, secret, room id, room code, seat id,
// identity id or segment id is written: a moment is its name, time and words, not the phrase it points at.
//
// The people, their colours and the action's colour are worked out by the SAME helpers the screen draws with
// (SummaryScreen.tsx imports them from here), so the file cannot say something the screen does not.
import { languageName } from "../data/languages";
import { catalogueLangOf, dirOf, uiLangFor, type Translate, type UiLang } from "../i18n";
import type { RecordData, Summary } from "../protocol/records";
import { SEAT_COLOURS } from "../room/model";

/** The seat colours of tokens.css, light then dark, in the model's slot order (gold, teal, rose, moss, plum, clay, cyan,
 * umber). The file cannot read the app's stylesheet, so it carries them; summaryFile.test.ts holds them equal to
 * tokens.css. */
export const FILE_SEAT_HEX: readonly { light: string; dark: string }[] = [
  { light: "#9A6A12", dark: "#E0B15A" },
  { light: "#0A7F73", dark: "#4FC3B5" },
  { light: "#B4456B", dark: "#E58AAA" },
  { light: "#3F7320", dark: "#A8CC72" },
  { light: "#A03A78", dark: "#DA93C8" },
  { light: "#A9542B", dark: "#E8A278" },
  { light: "#156C7C", dark: "#7FC0CE" },
  { light: "#7A5A3C", dark: "#C9A87F" },
];

/** The record's own colour slot for a seat id (join order, as the record and the live room colour it). */
export const colourOf = (record: RecordData, seatId: string): number | null => {
  const i = record.seats.findIndex((s) => s.id === seatId);
  return i < 0 ? null : i % SEAT_COLOURS;
};

/** An action's "who" in its seat colour: the first seat of that name; a named voice has no seat, and is drawn in ink. */
export const nameColour = (record: RecordData, name: string): number | null => {
  const i = record.seats.findIndex((x) => x.name.trim() === name.trim());
  return i < 0 ? null : i % SEAT_COLOURS;
};

export type Person = { key: string; name: string; colour: number; lang: string };

/** The people as the header shows them (consultant ruling 5, 2026-10-02). P-21's record seats carry no identity, so
 * until the summary is on screen the header shows the record's SEATS, in join order; once it is, the summary's
 * `record.people` - one per identity, each coloured by its first seat - so no identity shows twice. */
export function people(record: RecordData, summary: Summary | null): Person[] {
  if (summary) {
    return summary.record.people.map((p) => {
      const i = record.seats.findIndex((s) => s.id === p.seat);
      const seat = i < 0 ? undefined : record.seats[i];
      return { key: p.seat, name: p.name, colour: i < 0 ? 0 : i % SEAT_COLOURS, lang: seat?.speak_lang ?? "" };
    });
  }
  return record.seats.map((s, i) => ({ key: s.id, name: s.name, colour: i % SEAT_COLOURS, lang: s.speak_lang }));
}

/** The languages spoken, as the header lists them: each seat's speaking language once, in join order. */
export const spokenLangs = (record: RecordData): string[] => [...new Set(record.seats.map((s) => s.speak_lang))];

/** The moment the record started (its creation when it never started): the header's day and time, and the file name. */
export const recordStart = (record: RecordData): number => Date.parse(record.started ?? record.created);

/** HTML-escape a text for an element's content or a double-quoted attribute value. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/** The file's name: `majlis-summary-<YYYY-MM-DD>-<HHMM>-<reading language>.html`, the record's start in the phone's
 * own time zone (the same day and time the file's header shows), ASCII digits always (never Intl's), and the reading
 * language reduced to ASCII letters, digits and '-' so the same record's summary in two languages saves as two files.
 * Nothing else: no room id, no name (a name is the person's, not the file's). */
export function summaryFileName(record: RecordData, readLang: string): string {
  const at = new Date(recordStart(record));
  const day = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}${pad(at.getMinutes())}`;
  const lang = readLang.replace(/[^A-Za-z0-9-]/g, "").slice(0, 16) || "x";
  return `majlis-summary-${day}-${time}-${lang}.html`;
}

/** The MIME type of the saved file. */
export const SUMMARY_FILE_TYPE = "text/html";

// The screen's look, in plain CSS: the tokens.css light values, and its dark ones under prefers-color-scheme. No url(),
// no @import, no @font-face: fonts are named, never fetched (the phone's own or the system sans).
function css(): string {
  const seats = FILE_SEAT_HEX.map((c, i) => `.c${i}{--c:${c.light}}`).join("");
  const dark = FILE_SEAT_HEX.map((c, i) => `.c${i}{--c:${c.dark}}`).join("");
  return [
    ":root{--ground:#EDF1F6;--surface:#FFFFFF;--ink:#0F1B2A;--muted:#5A6878;--line:#DAE1EA;--on-dot:#FFFFFF;color-scheme:light dark}",
    seats,
    // after the light seats: the same specificity, so the later (dark) rule wins in a dark theme
    "@media (prefers-color-scheme: dark){:root{--ground:#0A0F16;--surface:#161D27;--ink:#E7ECF3;--muted:#8C99AA;--line:#232D3A;--on-dot:#0A0F16}" +
      dark +
      "}",
    '*{box-sizing:border-box}html{background:var(--ground)}body{margin:0;padding:16px;color:var(--ink);font-family:"IBM Plex Sans","IBM Plex Sans Arabic","PingFang SC","Noto Sans SC",system-ui,sans-serif;line-height:1.45}',
    // MAJLIS-164: the app's :lang() stacks (app.css, tokens.css), so a saved file in Japanese or Traditional Chinese
    // never takes the Simplified faces; Simplified last, as in the app
    ':lang(ja){font-family:"IBM Plex Sans","Hiragino Sans","Hiragino Kaku Gothic ProN","Yu Gothic","Meiryo","Noto Sans JP","Noto Sans CJK JP",system-ui,sans-serif}',
    ':lang(zh-Hant),:lang(zh-TW),:lang(zh-HK),:lang(zh-MO),:lang(yue){font-family:"IBM Plex Sans","PingFang TC","PingFang HK","Microsoft JhengHei","Noto Sans TC","Noto Sans CJK TC",system-ui,sans-serif}',
    ':lang(zh-Hans){font-family:"IBM Plex Sans","IBM Plex Sans Arabic","PingFang SC","Noto Sans SC",system-ui,sans-serif}',
    "main{max-width:640px;margin:0 auto;background:var(--surface);border-radius:12px;padding:14px 16px 16px}",
    "h1{font-size:18px;font-weight:600;margin:0 0 6px}",
    ".when{font-size:13px;font-weight:600;margin:0}",
    ".people{display:flex;align-items:center;flex-wrap:wrap;gap:6px;margin:4px 0 0;font-size:13px}",
    ".dot{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:var(--c);color:var(--on-dot);font-size:11px;font-weight:600}",
    ".who{color:var(--c,var(--ink));font-weight:600}",
    ".langs{font-size:12px;color:var(--muted);margin:4px 0 10px}",
    "section{border-top:1px solid var(--line);padding:8px 0 10px}",
    "h2{font-size:12px;font-weight:600;color:var(--muted);margin:0 0 4px}",
    "ol,ul{margin:0;padding-inline-start:18px}li{font-size:14px;margin:3px 0}",
    "ul.plain{list-style:none;padding:0}",
    ".action .who{margin-inline-end:10px}",
    ".moment-meta{display:flex;gap:8px;align-items:baseline;font-size:13px}.moment-meta time{color:var(--muted)}",
    "blockquote{margin:1px 0 6px;font-size:14px}",
    ".note{border-top:1px solid var(--line);padding-top:12px;margin:0;font-size:12px;color:var(--muted);text-align:center}",
  ].join("\n");
}

export interface SummaryFileInput {
  record: RecordData;
  summary: Summary;
  /** the reading language the summary was written in (P-40): <html lang> and dir */
  readLang: string;
  /** the interface language and its words, as the screen draws them (the chrome follows the reading language) */
  ui: { lang: UiLang; t: Translate };
}

/** The saved file's whole text. Pure: the same summary always makes the same file. */
export function summaryFileHtml({ record, summary, readLang, ui }: SummaryFileInput): string {
  const { lang, t } = ui;
  const e = escapeHtml;
  const at = recordStart(record);
  const day = new Intl.DateTimeFormat(lang, { weekday: "short", month: "short", day: "numeric" }).format(at);
  const time = (ms: number) => new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(ms);
  const dir = dirOf(readLang);
  // the chrome's words are the interface catalogue's; marked as such where that is not the reading language's own
  // (MAJLIS-164: zh-Hans reads its summary under English chrome until its catalogue lands, as the screen does)
  const words = catalogueLangOf(lang);
  const chromeLang = uiLangFor(readLang) === words ? "" : ` lang="${e(words)}" dir="${dirOf(words)}"`;
  const colourClass = (c: number | null) => (c === null ? "who" : `who c${c}`);
  const s = summary.sections;

  const list = (items: string[], numbered: boolean) => {
    const tag = numbered ? "ol" : "ul";
    return `<${tag}>${items.map((x) => `<li>${e(x)}</li>`).join("")}</${tag}>`;
  };
  const section = (title: string, body: string) => `<section><h2${chromeLang}>${e(title)}</h2>${body}</section>`;

  const parts: string[] = [];
  parts.push(section(t("summaryRecap"), list(s.recap, true)));
  if (s.topics.length > 0) parts.push(section(t("summaryTopics"), list(s.topics, true)));
  if (s.decisions.length > 0) parts.push(section(t("summaryDecisions"), list(s.decisions, false)));
  if (s.actions.length > 0) {
    const items = s.actions.map((a) => {
      const who = a.who ? `<span class="${colourClass(nameColour(record, a.who))}" dir="auto">${e(a.who)}</span>` : "";
      return `<li class="action">${who}<span class="what">${e(a.what)}</span></li>`;
    });
    parts.push(section(t("summaryActions"), `<ul class="plain">${items.join("")}</ul>`));
  }
  if (s.questions.length > 0) parts.push(section(t("summaryQuestions"), list(s.questions, false)));
  if (s.moments.length > 0) {
    const items = s.moments.map((m) => {
      const seat = record.phrases.find((p) => p.segment.id === m.segment_id)?.segment.seat ?? null;
      const c = seat === null ? null : colourOf(record, seat);
      return (
        `<li><span class="moment-meta"><span class="${colourClass(c)}" dir="auto">${e(m.name)}</span>` +
        `<time>${e(time(Date.parse(m.at)))}</time></span><blockquote>${e(m.text)}</blockquote></li>`
      );
    });
    parts.push(section(t("summaryMoments"), `<ul class="plain">${items.join("")}</ul>`));
  }

  const who = people(record, summary);
  const dots = who
    .map((p) => `<span class="dot c${p.colour}" aria-hidden="true">${e(Array.from(p.name.trim())[0] ?? "")}</span>`)
    .join("");
  const names = who.map((p) => `<span class="who c${p.colour}" dir="auto">${e(p.name)}</span>`).join(e(t("nameSep")));
  const langs = spokenLangs(record)
    .map((l) => languageName(l, lang))
    .join(" · ");
  const when = `${day} · ${time(at)} · ${t("summaryMinutes", { min: record.minutes })}`;
  const title = `${t("summary")} · ${day}`;

  return [
    "<!doctype html>",
    `<html lang="${e(readLang)}" dir="${dir}">`,
    "<head>",
    '<meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">`,
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${e(title)}</title>`,
    `<style>\n${css()}\n</style>`,
    "</head>",
    "<body>",
    "<main>",
    `<h1${chromeLang}>${e(t("summary"))}</h1>`,
    `<p class="when"${chromeLang}>${e(when)}</p>`,
    `<p class="people">${dots} ${names}</p>`,
    `<p class="langs"${chromeLang}>${e(langs)}</p>`,
    ...parts,
    `<p class="note"${chromeLang}>${e(t("summaryGenerated"))}</p>`,
    "</main>",
    "</body>",
    "</html>",
    "",
  ].join("\n");
}

/** How the phone hands a file on: the system share sheet where it takes files, otherwise a download. Injected (AppDeps
 * `saveFile`) so tests drive it; browserSaver() is the real one. */
export interface FileSaver {
  /** navigator.canShare({ files: [file] }) - false where there is no canShare or it refuses files */
  canShare: (file: File) => boolean;
  /** navigator.share({ files: [file] }); rejects with an AbortError when the person cancels */
  share: (file: File) => Promise<void>;
  /** an <a download> of an object URL, clicked once and revoked */
  download: (file: File) => void;
}

export type SaveOutcome = "shared" | "downloaded" | "cancelled" | "failed";

/** Save one file. Called straight from the tap (no await before share: the share sheet needs the tap's activation).
 * A cancelled share is not a failure; any other error is, and the caller shows it. */
export async function saveFile(saver: FileSaver, file: File): Promise<SaveOutcome> {
  try {
    if (saver.canShare(file)) {
      await saver.share(file);
      return "shared";
    }
    saver.download(file);
    return "downloaded";
  } catch (err) {
    if ((err as { name?: unknown } | null)?.name === "AbortError") return "cancelled";
    return "failed";
  }
}

/** The phone's own share sheet and download. */
export function browserSaver(win: Window & typeof globalThis = window): FileSaver {
  // the last download's object URL, revoked at the next one: revoking right after the click can cut a download short
  // on some browsers, and a summary file is a few kilobytes
  let last: string | null = null;
  return {
    canShare: (file) => {
      const nav = win.navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      if (typeof nav.canShare !== "function" || typeof nav.share !== "function") return false;
      try {
        return nav.canShare({ files: [file] });
      } catch {
        return false;
      }
    },
    share: (file) => win.navigator.share({ files: [file] }),
    download: (file) => {
      if (last !== null) win.URL.revokeObjectURL(last);
      const url = win.URL.createObjectURL(file);
      last = url;
      const a = win.document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.rel = "noopener";
      a.hidden = true;
      win.document.body.appendChild(a);
      try {
        a.click();
      } finally {
        a.remove();
      }
    },
  };
}
